import { useMemo } from 'react';

import { di } from 'react-magnetic-di';
import { type Environment } from 'react-relay';

import { AvpDashboardAri } from '@atlassian/ari/avp';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { createStandaloneChart as createStandaloneChartRelay } from '../../common/relay/queries/standaloneCharts';
import { getActivationIdsByCloudId } from '../../common/relay/queries/tenant';
import { useRelayEnvironment } from '../../common/relay/RelayProvider';
import { getDashboardContextFromUrl } from '../../utils/dashboard-url';
import {
	CREATE_CHART_TO_DASHBOARD_MUTATION,
	CREATE_STANDALONE_CHART_QUERY,
	getActivationIdMappingByCloudId,
	resolveWorkspaceIdForMetric,
	resolveWorkspaceIdForMetricWithMapping,
	transformChartPayloadToAvpInput,
} from '../../utils/graphql';
import type {
	ChartLinkPayload,
	ChartLinkResponse,
	CreateChartToDashboardCanvasLayout,
	CreateChartToDashboardChart,
	CreateChartToDashboardResponse,
	CreateStandaloneChartResponse,
	WorkspaceToWorkspaceIdRecord,
} from '../../utils/graphql/types';

/**
 * Resolves activation ID mapping using Relay when available, otherwise falls back to performGqlFetch.
 */
async function resolveActivationIdMapping(
	cloudId: string,
	relayEnvironment: Environment | null,
): Promise<WorkspaceToWorkspaceIdRecord> {
	if (relayEnvironment) {
		const data = await getActivationIdsByCloudId(relayEnvironment, cloudId);
		const tenantContext = data.tenantContexts?.[0];
		if (!tenantContext) {
			throw new Error(`No tenant context found for cloud ID: ${cloudId}`);
		}
		return (tenantContext.activationIds ?? []).reduce((acc, activationId) => {
			if (activationId.product && activationId.active) {
				acc[activationId.product] = activationId.active;
			}
			return acc;
		}, {} as WorkspaceToWorkspaceIdRecord);
	}
	return getActivationIdMappingByCloudId(cloudId);
}

async function resolveRovoWorkspaceAri(
	cloudId: string,
	relayEnvironment: Environment | null,
): Promise<string | undefined> {
	try {
		const activationIdMap = await resolveActivationIdMapping(cloudId, relayEnvironment);
		const rovoActivationId = activationIdMap['rovo'];
		if (rovoActivationId) {
			return `ari:cloud:rovo::workspace/${rovoActivationId}`;
		}
	} catch {
		// Non-fatal: fall back to region routing if we can't resolve the Rovo activation ID
	}
	return undefined;
}

async function createStandaloneChartGraphQL(
	chart: ChartLinkPayload,
	cloudId: string,
	relayEnvironment: Environment | null,
	chartId?: string,
): Promise<CreateStandaloneChartResponse> {
	let workspaceId: string | undefined;

	if (chart.metricsConfig?.metricNames?.[0]) {
		workspaceId = await resolveWorkspaceIdForMetric(cloudId, chart.metricsConfig.metricNames[0]);
	}

	const workspaceAri = await resolveRovoWorkspaceAri(cloudId, relayEnvironment);

	const avpChartInput = transformChartPayloadToAvpInput(chart, workspaceId, chartId);

	if (relayEnvironment) {
		const response = await createStandaloneChartRelay(relayEnvironment, {
			chart: avpChartInput,
			cloudId,
			...(workspaceAri ? { workspaceAri } : {}),
		});
		return response as CreateStandaloneChartResponse;
	}

	const response = await performGqlFetch<
		CreateStandaloneChartResponse,
		{ chart: Record<string, unknown>; cloudId: string; workspaceAri?: string }
	>({
		gqlQuery: CREATE_STANDALONE_CHART_QUERY,
		variables: {
			chart: avpChartInput,
			cloudId,
			...(workspaceAri ? { workspaceAri } : {}),
		},
	});

	return response.data;
}

function getCurrentUrl(): URL {
	if (typeof window === 'undefined' || !window.location) {
		throw new Error('Window location is not available');
	}
	return new URL(window.location.href);
}

async function fetchCloudId(): Promise<string | null> {
	try {
		const { origin } = getCurrentUrl();
		const response = await fetch(`${origin}/_edge/tenant_info`, {
			method: 'GET',
			headers: { Accept: 'application/json' },
			credentials: 'include',
		});

		if (!response.ok) {
			return null;
		}

		const json = await response.json();
		return json.cloudId || null;
	} catch {
		return null;
	}
}

/**
 * Service for chart-specific API calls within the viz package
 */
export class ChartApiService {
	private cloudId: string | null = null;
	private relayEnvironment: Environment | null;

	constructor(cloudId?: string, relayEnvironment?: Environment | null) {
		if (cloudId) {
			this.cloudId = cloudId;
		}
		this.relayEnvironment = relayEnvironment ?? null;
	}

	private async getCloudId(): Promise<string | null> {
		if (!this.cloudId) {
			this.cloudId = await fetchCloudId();
		}
		return this.cloudId;
	}

	/**
	 * Generate a shareable link for chart data
	 */
	async generateChartLink(payload: ChartLinkPayload): Promise<ChartLinkResponse> {
		const cloudId = await this.getCloudId();
		if (!cloudId) {
			throw new Error('cloudId is required for chart creation');
		}

		const graphqlResult = await createStandaloneChartGraphQL(
			payload,
			cloudId,
			this.relayEnvironment,
		);
		return {
			link: graphqlResult.avp_createStandaloneChart.avpVizUrl,
			chartId: graphqlResult.avp_createStandaloneChart.chart.id,
		};
	}

	async exportChartToDashboard(
		payload: ChartLinkPayload,
		targetDashboardAri: string,
		dataFetchStrategy: 'STATIC' | 'DYNAMIC' = 'STATIC',
	): Promise<{
		chart: CreateChartToDashboardChart | null;
		canvasLayout: CreateChartToDashboardCanvasLayout | null;
	}> {
		const cloudId = await this.getCloudId();
		if (!cloudId) {
			throw new Error('cloudId is required to export a chart to a dashboard');
		}

		let workspaceId: string | null | undefined = null;
		if (payload.metricsConfig?.metricNames?.[0]) {
			try {
				workspaceId = await resolveWorkspaceIdForMetric(
					cloudId,
					payload.metricsConfig.metricNames[0],
				);
			} catch {
				workspaceId = null;
			}
		}

		const avpChartInput = transformChartPayloadToAvpInput(
			payload,
			workspaceId || '',
			undefined,
			dataFetchStrategy,
		);

		const response = await performGqlFetch<
			CreateChartToDashboardResponse,
			{ dashboardAri: string; chart: unknown }
		>({
			gqlQuery: CREATE_CHART_TO_DASHBOARD_MUTATION,
			variables: {
				dashboardAri: targetDashboardAri,
				chart: avpChartInput,
			},
		});

		const result = response.data?.avp_createChart;
		return {
			chart: result?.chart ?? null,
			canvasLayout: result?.canvasLayout ?? null,
		};
	}

	async addChartToDashboard(payload: ChartLinkPayload): Promise<{
		chart: CreateChartToDashboardChart | null;
		canvasLayout: CreateChartToDashboardCanvasLayout | null;
	}> {
		const dashboardContext = getDashboardContextFromUrl();
		if (!dashboardContext) {
			throw new Error(
				'Not on a dashboard page. Add to Dashboard is only available when viewing or editing a dashboard.',
			);
		}

		const cloudId = dashboardContext.cloudId;
		let activationIdMap: WorkspaceToWorkspaceIdRecord;
		try {
			activationIdMap = await resolveActivationIdMapping(cloudId, this.relayEnvironment);
		} catch (error) {
			throw new Error(
				`Failed to get activation ID mapping for dashboard: ${
					error instanceof Error ? error.message : String(error)
				}`,
			);
		}

		// getDashboardContextFromUrl ensures we are on a home dashboard
		const activationId = activationIdMap['townsquare'];
		if (!activationId) {
			throw new Error(
				'Could not resolve activationId for dashboard from tenant context (no activation IDs in mapping).',
			);
		}

		let workspaceId: string | null | undefined = null;
		if (payload.metricsConfig?.metricNames?.[0]) {
			try {
				workspaceId = resolveWorkspaceIdForMetricWithMapping(
					activationIdMap,
					payload.metricsConfig.metricNames[0],
				);
			} catch {}
		}

		const avpChartInput = transformChartPayloadToAvpInput(payload, workspaceId || '');

		let dashboardAri;
		try {
			dashboardAri = AvpDashboardAri.create({
				siteId: cloudId,
				activationId,
				dashboardId: dashboardContext.dashboardId,
			});
		} catch (ariError) {
			const message =
				ariError instanceof Error ? ariError.message : 'Invalid dashboard URL context';
			throw new Error(`Failed to build dashboard ARI from URL context: ${message}`);
		}

		const response = await performGqlFetch<
			CreateChartToDashboardResponse,
			{ dashboardAri: string; chart: unknown }
		>({
			gqlQuery: CREATE_CHART_TO_DASHBOARD_MUTATION,
			variables: {
				dashboardAri: dashboardAri.toString(),
				chart: avpChartInput,
			},
		});

		const result = response.data?.avp_createChart;
		return {
			chart: result.chart ?? null,
			canvasLayout: result.canvasLayout ?? null,
		};
	}
}

/**
 * Hook to get chart API service for production use
 * For development/testing with mocks, use the service from '@atlassian/conversation-assistant-viz/mocks'
 *
 * @param cloudId - Optional cloud ID to use instead of fetching from /_edge/tenant_info.
 *                  Should be provided when running on untenanted domains (e.g. Atlas Home).
 */
export const useChartApiService = (cloudId?: string): ChartApiService => {
	di(ChartApiService);

	const relayEnvironment = useRelayEnvironment();

	// Memoize the service instance to prevent re-creation on every render
	return useMemo(() => new ChartApiService(cloudId, relayEnvironment), [cloudId, relayEnvironment]);
};

// Mock service is available through '@atlassian/conversation-assistant-viz/test-utils' export path
